import {
  IAvalaraTaxConnectionSchema,
  ILegalEntityResponseSchema,
  ITaxConnectionSchema,
  LegalEntityApplicationScope,
  TaxProviderTypeEnum,
  TaxStatusEnum,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { MdOpenInNew } from 'react-icons/md';
import {
  useDeleteEntity,
  usePerformEntityAction,
} from '../../../../../../api/queryUtils';
import { settingsQueryKeys } from '../../../../../../api/settingsService';
import {
  TAX_ENVIRONMENTS_DISPLAY,
  TAX_IDENTIFIER_DISPLAY,
} from '../../../../../../constants/taxes';
import { TaxCardEdit } from '../TaxCardEdit';
import { TaxCardAction, TaxCardView } from '../TaxCardView';
import { AvalaraForm } from './AvalaraForm';

interface AvalaraTaxConnectionProps {
  connection: IAvalaraTaxConnectionSchema;
  legalEntities: Record<string, ILegalEntityResponseSchema>;
  verifyConnection: (provider: TaxProviderTypeEnum) => Promise<void>;
  isAvalaraConnectionLoading: boolean;
}

export const AvalaraTaxConnection: FC<AvalaraTaxConnectionProps> = ({
  connection,
  legalEntities,
  verifyConnection,
  isAvalaraConnectionLoading,
}) => {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<'VIEW' | 'EDIT'>('VIEW');

  const { mutateAsync: doUpdateStatus } =
    usePerformEntityAction<ITaxConnectionSchema>('tax', {
      meta: { showErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: settingsQueryKeys.tax() });
      },
    });
  const { mutateAsync: doDeleteConnection } = useDeleteEntity('tax', {
    meta: { showErrorToast: true },
  });
  const isActive = connection.status === TaxStatusEnum.ACTIVE;

  const handleAction = async (action: TaxCardAction) => {
    switch (action) {
      case 'STATUS_UPDATE':
        return doUpdateStatus({
          id: TaxProviderTypeEnum.AVALARA,
          action: isActive ? TaxStatusEnum.INACTIVE : TaxStatusEnum.ACTIVE,
        });
      case 'VERIFY':
        await verifyConnection(TaxProviderTypeEnum.AVALARA);
        break;
      case 'DELETE':
        await doDeleteConnection({ id: TaxProviderTypeEnum.AVALARA });
        break;
      case 'OPEN':
        window.open('https://www.avalara.com/', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <>
      {mode === 'VIEW' && (
        <TaxCardView
          mb="4"
          status={connection.status}
          actions={[
            {
              action: 'STATUS_UPDATE',
              label: isActive ? 'Disable Connection' : 'Enable Connection',
              isDanger: false,
            },
            { action: 'VERIFY', label: 'Verify Connection' },
            { action: 'OPEN', label: 'Avalara', icon: MdOpenInNew },
            { action: 'DELETE', label: 'Delete', isDanger: true },
          ]}
          tags={[
            {
              label: 'Account Number',
              value: connection.credentials?.accountNumber || '',
            },
            {
              label: 'Environment',
              value: connection.credentials?.environment
                ? TAX_ENVIRONMENTS_DISPLAY[connection.credentials.environment]
                : '',
            },
            {
              label: 'Tax Calculation Field',
              value: connection.configurations.identifier
                ? TAX_IDENTIFIER_DISPLAY[connection.configurations.identifier]
                : '',
            },
            {
              label: 'Company Code',
              value: connection.credentials?.companyCode || '',
            },
            {
              label: 'License Key',
              value: connection.credentials?.licenseKey
                ? connection.credentials.licenseKey.substring(0, 4) + '****'
                : '',
              colSpan: 2,
            },
            {
              label: 'Post and Commit Transactions',
              value: connection.configurations.postAndCommitTransactions
                ? 'True'
                : 'False',
              colSpan: 2,
            },
            {
              label: 'Apply to Which Legal Entities',
              value:
                connection.configurations.legalEntityOverride ===
                LegalEntityApplicationScope.ALL
                  ? 'All Legal Entities'
                  : connection.configurations.legalEntities
                      .map((id) => legalEntities[id]?.name ?? id)
                      .join(', '),
              colSpan: 2,
            },
          ]}
          type={TaxProviderTypeEnum.AVALARA}
          onEdit={() => setMode('EDIT')}
          onAction={handleAction}
          tagsContainer={{
            columnGap: 4,
            templateColumns: '9.375rem 1fr',
          }}
        />
      )}
      {mode === 'EDIT' && (
        <TaxCardEdit type={TaxProviderTypeEnum.AVALARA}>
          <AvalaraForm
            connection={connection}
            handleCancel={() => setMode('VIEW')}
          />
        </TaxCardEdit>
      )}
    </>
  );
};
